/* eslint-disable default-case */
import { createSlice } from '@reduxjs/toolkit';
import { arrayToDictionary } from 'scenes/Settings/FormEditor/utils/arrayToDictionary';
import v4 from 'uuid/v4';
import {
  acceptChatbotTermsThunk,
  getChatbotThreadsThunk,
  getHaveIAcceptedChatbotTermsThunk,
} from '../thunks/chatbotThunks';
import { getThreadsByType } from './utils/getThreadsByType';

const mockthread = {
  businessId: '', // threadit haetaan business id:n perusteella
  id: '',
  visibility: 'public' | 'private', // kummast chatbotist tullu
  projectKey: '',
  discussion: [], //
};

export const chatbotSlice = createSlice({
  name: 'chatbot',
  initialState: {
    threads: {}, // hashmap of threads
    selectedThreadId: null,
    discussion: [], // [{ id, role: 'user'|'assistant', content, status, etc }]

    isChatbotOpen: false,
    isChatbotFullscreen: false,
    isLoading: false,
    isSendMessageLoading: false,
    error: null,
    userHasAcceptedTerms: false,
    isAdmin: false,
    isBulletin: false,
  },
  reducers: {
    startNewMessagingThread: (state, action) => {
      console.log('startNewMessagingThread');
      state.discussion = [];
      state.selectedThreadId = null;
    },
    selectThread: (state, action) => {
      console.log('selectThread');
      state.selectedThreadId = action.payload;
      const selectedThread = state.threads[action.payload];
      state.discussion = selectedThread.messages;
    },
    closeChatbot: (state, action) => {
      console.log('closeChatbot');
      state.isChatbotOpen = false;
      state.discussion = [];
      state.selectedThreadId = null;
      state.isBulletin = false;
      state.isAdmin = false;
    },
    toggleOpenCloseAdminChatbot: (state, action) => {
      console.log('toggleOpenCloseAdminChatbot');
      state.isChatbotOpen = !state.isChatbotOpen;
      state.isAdmin = true;
    },
    toggleOpenCloseBulletinChatbot: (state, action) => {
      console.log('toggleOpenCloseBulletinChatbot');
      state.isChatbotOpen = !state.isChatbotOpen;
      state.isBulletin = true;
      state.isAdmin = false;
    },
    toggleOpenCloseChatbot: (state, action) => {
      console.log('toggleOpenCloseChatbot');
      state.isChatbotOpen = !state.isChatbotOpen;
      state.isAdmin = false;
    },
    toggleFullScreen: (state, action) => {
      console.log('onToggleFullScreen');
      state.isChatbotFullscreen = !state.isChatbotFullscreen;
    },

    cleanChatbotDiscussionAndClose: (state, action) => {
      console.log('cleanChatbotDiscussionAndClose');
      state.discussion = [];
      state.isChatbotOpen = false;
      state.selectedThreadId = null;
    },

    sendMessageToAi: (state, action) => {
      state.isLoading = true;
      const { text, yearMonth, threadId } = action.payload;
      const now = Date.now();

      state.selectedThreadId = threadId;

      // 1) push the user message
      state.discussion.push({
        id: v4(),
        role: 'user',
        content: text,
        status: 'final',
        timeStamp: now,
      });

      // 2) push the assistant placeholder (the one we'll fill with streaming deltas)
      const turnId = v4();
      state.discussion.push({
        id: turnId,
        role: 'assistant',
        content: '',
        status: 'streaming',
        yearMonth, // HASH KEY "2025-10#uuid"
        timeStamp: now,
        citations: [],
      });

      state.error = null;
    },

    // Stream events from the backend: { type: 'delta'|'done'|'error', }
    aiResponseMessageStream: (state, action) => {
      const evt = action.payload;
      console.log('THIS IS WHAT WE GET FROM SOCKET:::');
      console.log(evt);

      // always target the LAST message; it should be the assistant placeholder
      let lastIndex = state.discussion.length - 1;
      let last = state.discussion[lastIndex];

      if (evt.type === 'delta' && typeof evt.text === 'string') {
        last.content += evt.text;
      }

      if (evt.type === 'done') {
        last.status = 'final';
        last.citations = evt.rawCitations;

        // check does this thread already exists
        const doesThreadAlreadyExists = state.threads[state.selectedThreadId];

        if (!doesThreadAlreadyExists) {
          state.threads = {
            ...state.threads,
            [evt.threadItem.id]: evt.threadItem,
          };
        }

        // message property does not exist on new threads
        const currentThread = state.threads[state.selectedThreadId];
        if (!currentThread.messages) {
          currentThread.messages = [];
        }

        const ts = new Date(evt.messageItem.id.split('#')[0]).getTime();
        // user message
        currentThread.messages.push({
          id: v4(),
          threadId: evt.messageItem.threadId,
          role: 'user',
          content: evt.messageItem.userMessage,
          status: 'final',
          yearMonth: evt.messageItem.yearMonth,
          timeStamp: ts,
        });

        // assistant message
        currentThread.messages.push({
          id: v4(),
          role: 'assistant',
          content: evt.messageItem.assistantMessage,
          status: 'final',
          yearMonth: evt.messageItem.yearMonth,
          timeStamp: ts,
          citations: evt.messageItem.citations ?? [],
        });

        state.isLoading = false;
      }

      if (evt.type === 'error') {
        state.isLoading = false;
        last.status = 'error';
        state.error = evt.message || 'AI error';
        if (evt.message) {
          last.content = last.content
            ? `${last.content}\n\n[Error] ${evt.message}`
            : `[Error] ${evt.message}`;
        }
      }
    },
  },
  extraReducers(builder) {
    builder
      // GET
      .addCase(getHaveIAcceptedChatbotTermsThunk.pending, (state, action) => {})
      .addCase(getHaveIAcceptedChatbotTermsThunk.fulfilled, (state, action) => {
        const res = action.payload;
        console.log('getHaveIAcceptedChatbotTermsThunk RESPONSE::');
        console.log(res);

        state.userHasAcceptedTerms = res.hasAccepted;
      })
      .addCase(
        getHaveIAcceptedChatbotTermsThunk.rejected,
        (state, action) => {}
      )

      // GET Threads
      .addCase(getChatbotThreadsThunk.pending, (state, action) => {})
      .addCase(getChatbotThreadsThunk.fulfilled, (state, action) => {
        const threads = action.payload;
        const hashMap = arrayToDictionary(threads);
        state.threads = hashMap;
      })
      .addCase(getChatbotThreadsThunk.rejected, (state, action) => {})

      // POST
      .addCase(acceptChatbotTermsThunk.pending, (state, action) => {})
      .addCase(acceptChatbotTermsThunk.fulfilled, (state, action) => {
        const res = action.payload;
        console.log('acceptChatbotTermsThunk RESPONSE::');
        console.log(res);

        state.userHasAcceptedTerms = res.hasAccepted;
      })
      .addCase(acceptChatbotTermsThunk.rejected, (state, action) => {});
  },
});

// ACTIONS
export const {
  toggleOpenCloseAdminChatbot,
  toggleFullScreen,
  sendMessageToAi,
  aiResponseMessageStream,
  cleanChatbotDiscussionAndClose,
  closeChatbot,
  toggleOpenCloseChatbot,
  startNewMessagingThread,
  selectThread,
  toggleOpenCloseBulletinChatbot,
} = chatbotSlice.actions;

// SELECTORS
export const getChatbotControlData = (state) => {
  const slice = state.chatbotSlice;

  return {
    isChatbotOpen: slice?.isChatbotOpen,
    isChatbotFullscreen: slice?.isChatbotFullscreen,
    discussion: slice?.discussion,
    isLoading: slice?.isLoading,
    isAdmin: slice?.isAdmin,
    isBulletin: slice?.isBulletin,
    userHasAcceptedTerms: slice?.userHasAcceptedTerms,
    selectedThreadId: slice?.selectedThreadId,
    threads: getThreadsByType(Object.values(slice?.threads), slice?.isBulletin),
  };
};
